/**
 * reportCharts — the Chart.js 2.8 configs of the two Practice Ops report charts (dataviz skill, 2026-10-30).
 *
 * Plan §6 Report: chart.js 2.8, drawn through OpsReportChart.vue's destroy-before-redraw pattern (the one in
 * TeamInsights/components/ChartJSBarChart.vue). This file is the part that can be tested without a canvas: which colours, which
 * mark sizes, which scales and tooltips. The page's light-only teal language is the palette (this page is not themed):
 *
 *   ONE series (the average wait)  one accent, teal #0f7259 — a 2px line, 9px dots with a 2px white ring, a ~10% wash. No legend
 *                                  box: the card title names it. ONE y axis (never a second scale): the over-threshold count
 *                                  rides in the tooltip and in the table view, not on a second axis.
 *   the threshold                  a reference line, not a series: dotted amber #cb8309 (3:1 on white), drawn by a plugin so it also
 *                                  shows when the chart has a single point. The card header keys it ("threshold 15 min").
 *   TWO series (booked vs actual)  the EMPHASIS form: de-emphasis gray #c8cfcd for what was booked, the accent for what happened.
 *                                  A legend is in the card header (two series always have one); values sit at the bar tip. The
 *                                  gray is 1.5:1 on white, which the palette validator WARNs on, so the relief is mandatory and
 *                                  present: a visible value on every bar AND the table twin the card can switch to.
 *   chrome                         solid 1px gridlines #eef2f1, axis text in the muted ink #707876 — text never wears the series
 *                                  colour. Bars are capped at 24px with a 2px white gap between neighbours; hit targets are 24px+.
 *
 * Chart.js 2.8 has no rounded bar ends, so bars are square; everything else follows the skill's mark specs.
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file.
 */

export const COLORS = Object.freeze({
  accent: '#0f7259',
  accentWash: 'rgba(15, 114, 89, 0.10)',
  deemphasis: '#c8cfcd',
  threshold: '#cb8309',
  grid: '#eef2f1',
  axis: '#dfe4e3',
  ink: '#0c0f0e',
  inkSub: '#3a4240',
  inkMuted: '#707876',
  surface: '#ffffff',
  crosshair: '#c8cfcd',
});

const FONT = "Hanken Grotesk, -apple-system, 'Segoe UI', Roboto, sans-serif";

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

function minutes(v) {
  if (!isNum(v)) return '—';
  const m = Math.round(v);
  if (m < 60) return `${m} min`;
  return m % 60 ? `${Math.floor(m / 60)} hr ${m % 60} min` : `${Math.floor(m / 60)} hr`;
}

/** A clean upper bound for the y axis: room above the tallest value (and above the threshold line, so it is always on the plot). */
export function axisMax(values, thresholdMin) {
  const top = Math.max(0, ...values.filter(isNum), isNum(thresholdMin) ? thresholdMin : 0);
  const padded = top * 1.15;
  const step = padded <= 30 ? 5 : padded <= 120 ? 10 : 30;
  return Math.max(step * 2, Math.ceil(padded / step) * step);
}

/** Draws the threshold as a dotted amber reference line across the plot (under the marks). */
export function thresholdPlugin(thresholdMin) {
  return {
    id: 'opsThreshold',
    beforeDatasetsDraw(chart) {
      const scale = chart.scales && chart.scales['y-axis-0'];
      const area = chart.chartArea;
      if (!scale || !area || !isNum(thresholdMin)) return;
      const y = scale.getPixelForValue(thresholdMin);
      if (!(y >= area.top && y <= area.bottom)) return;
      const ctx = chart.ctx;
      ctx.save();
      ctx.strokeStyle = COLORS.threshold;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.setLineDash([1, 5]);
      ctx.beginPath();
      ctx.moveTo(area.left, y);
      ctx.lineTo(area.right, y);
      ctx.stroke();
      ctx.restore();
    },
  };
}

/** A vertical hairline that snaps to the hovered X, so a reader aims at a day, never at a 2px line. */
export const crosshairPlugin = {
  id: 'opsCrosshair',
  afterDatasetsDraw(chart) {
    const tip = chart.tooltip;
    const active = tip && tip._active;
    const area = chart.chartArea;
    if (!active || !active.length || !active[0]._view || !area) return;
    const x = active[0]._view.x;
    const ctx = chart.ctx;
    ctx.save();
    ctx.strokeStyle = COLORS.crosshair;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, area.top);
    ctx.lineTo(x, area.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

/** The value at the tip of every bar (ink in a text token — never the bar's own colour). */
export const barValuePlugin = {
  id: 'opsBarValues',
  afterDatasetsDraw(chart) {
    const ctx = chart.ctx;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    chart.data.datasets.forEach((dataset, di) => {
      const meta = chart.getDatasetMeta(di);
      if (!meta || meta.hidden) return;
      ctx.font = `${di === 1 ? '700' : '500'} 11.5px ${FONT}`;
      ctx.fillStyle = di === 1 ? COLORS.ink : COLORS.inkMuted;
      meta.data.forEach((bar, i) => {
        const value = dataset.data[i];
        if (!isNum(value) || !bar || !bar._model) return;
        ctx.fillText(String(Math.round(value)), bar._model.x, bar._model.y - 4);
      });
    });
    ctx.restore();
  },
};

const tooltipBase = {
  mode: 'index',
  intersect: false,
  backgroundColor: COLORS.ink,
  titleFontFamily: FONT,
  bodyFontFamily: FONT,
  titleFontSize: 12,
  bodyFontSize: 13,
  titleFontStyle: '600',
  bodyFontStyle: '700',
  titleMarginBottom: 6,
  xPadding: 10,
  yPadding: 8,
  cornerRadius: 8,
  displayColors: false,
  caretPadding: 8,
};

function scalesFor({ yMax, yTitle }) {
  return {
    xAxes: [{
      gridLines: { display: false, drawBorder: true, color: COLORS.grid, zeroLineColor: COLORS.axis },
      ticks: {
        fontColor: COLORS.inkMuted, fontFamily: FONT, fontSize: 11.5, autoSkip: true, maxTicksLimit: 12, maxRotation: 0, minRotation: 0, padding: 6,
      },
    }],
    yAxes: [{
      gridLines: { display: true, drawBorder: false, color: COLORS.grid, zeroLineColor: COLORS.axis, lineWidth: 1 },
      scaleLabel: yTitle ? { display: true, labelString: yTitle, fontColor: COLORS.inkMuted, fontFamily: FONT, fontSize: 11 } : { display: false },
      ticks: {
        beginAtZero: true, suggestedMax: yMax, precision: 0, fontColor: COLORS.inkMuted, fontFamily: FONT, fontSize: 11.5, padding: 8, maxTicksLimit: 6,
      },
    }],
  };
}

/**
 * The "Average wait" line (one series). `series` is report.js waitSeries().
 * @returns {{type: 'line', data: object, options: object, plugins: object[]}}
 */
export function waitLineConfig(series, thresholdMin) {
  const s = series && typeof series === 'object' ? series : {};
  const labels = Array.isArray(s.labels) ? s.labels : [];
  const avg = Array.isArray(s.avg) ? s.avg : [];
  const over = Array.isArray(s.over) ? s.over : [];
  const visits = Array.isArray(s.visits) ? s.visits : [];
  const limit = isNum(thresholdMin) && thresholdMin > 0 && thresholdMin < 999 ? thresholdMin : null;

  return {
    type: 'line',
    data: {
      labels,
      datasets: [{
        label: 'Average wait',
        data: avg,
        borderColor: COLORS.accent,
        backgroundColor: COLORS.accentWash,
        borderWidth: 2,
        borderJoinStyle: 'round',
        borderCapStyle: 'round',
        lineTension: 0,
        fill: 'origin',
        spanGaps: false,
        pointRadius: 4.5,
        pointHoverRadius: 6,
        pointHitRadius: 24,
        pointBorderWidth: 2,
        pointBackgroundColor: COLORS.accent,
        pointBorderColor: COLORS.surface,
        pointHoverBackgroundColor: COLORS.accent,
        pointHoverBorderColor: COLORS.surface,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 0 },
      hover: { mode: 'index', intersect: false, animationDuration: 0 },
      legend: { display: false },
      title: { display: false },
      layout: { padding: { top: 8, right: 8 } },
      scales: scalesFor({ yMax: axisMax(avg, limit), yTitle: 'Minutes' }),
      tooltips: Object.assign({}, tooltipBase, {
        callbacks: {
          title: (items) => {
            const i = items && items[0] ? items[0].index : 0;
            const n = visits[i];
            return `${labels[i] || ''}${isNum(n) ? ` · ${n} ${n === 1 ? 'visit' : 'visits'}` : ''}`;
          },
          label: (item) => (isNum(item.yLabel) ? `${minutes(item.yLabel)} average wait` : 'No wait recorded'),
          afterLabel: (item) => {
            const n = over[item.index];
            if (!limit || !isNum(n)) return '';
            return `${n} over ${limit} min`;
          },
        },
      }),
    },
    plugins: [limit !== null ? thresholdPlugin(limit) : null, crosshairPlugin].filter(Boolean),
  };
}

/**
 * The "Booked vs actual chair time" bars (two series: booked in the de-emphasis gray, actual in the accent). `rows` is report.js
 * chairChartRows(providerRows(report)).
 * @returns {{type: 'bar', data: object, options: object, plugins: object[]}}
 */
export function chairBarsConfig(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const labels = list.map((r) => r.name);
  const booked = list.map((r) => r.avgBookedMin);
  const actual = list.map((r) => r.avgInChairMin);
  const both = [...booked, ...actual];
  const bar = (label, data, color) => ({
    label,
    data,
    backgroundColor: color,
    hoverBackgroundColor: color,
    borderColor: COLORS.surface,
    borderWidth: 2,
    maxBarThickness: 24,
  });
  return {
    type: 'bar',
    data: { labels, datasets: [bar('Booked', booked, COLORS.deemphasis), bar('Actual', actual, COLORS.accent)] },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 0 },
      hover: { mode: 'index', intersect: false, animationDuration: 0 },
      legend: { display: false },
      title: { display: false },
      layout: { padding: { top: 22, right: 8 } },
      scales: Object.assign(scalesFor({ yMax: axisMax(both, null), yTitle: 'Minutes' }), {
        xAxes: [{
          gridLines: { display: false, drawBorder: true, color: COLORS.grid, zeroLineColor: COLORS.axis },
          categoryPercentage: 0.7,
          barPercentage: 0.95,
          ticks: { fontColor: COLORS.inkSub, fontFamily: FONT, fontSize: 12, autoSkip: false, maxRotation: 0, minRotation: 0, padding: 6 },
        }],
      }),
      tooltips: Object.assign({}, tooltipBase, {
        callbacks: {
          title: (items) => (items && items[0] ? items[0].xLabel : ''),
          label: (item) => `${item.datasetIndex === 0 ? 'Booked' : 'Actual'}: ${minutes(item.yLabel)}`,
        },
      }),
    },
    plugins: [barValuePlugin],
  };
}
